import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { documentsApi } from 'domain/documents/api';
import { documentCategorizationAllowedSelector } from 'domain/documents/selectors/documentCategorizationAllowedSelector';
import type { SearchDoctype, SearchSubtypeChip } from 'domain/documents/schemas';
import { queryOptions } from 'services/query';

import { documentsKeys } from './keys';

type DocumentsAggregationsParams = {
  companyId: string;
  category: string;
  isConfidential: boolean;
  query: Record<string, unknown>;
  subtype: string | null;
};

const EMPTY_TABS: SearchDoctype[] = [];
const EMPTY_SUBTYPES: SearchSubtypeChip[] = [];

// documents/aggregations (da-19295 D9): doctype tabs + sub-type chips + active-category count
// under the GLOBAL filter only. companyId is in the KEY (per-company cache isolation — there is
// no cache reset on company switch, see domain/CLAUDE.md) but NOT in the request; the CompanyId
// header carries it.
export const documentsAggregationsQuery = ({
  companyId,
  category,
  isConfidential,
  query,
  subtype,
}: DocumentsAggregationsParams) =>
  queryOptions({
    queryKey: documentsKeys.documentsAggregations(companyId, category, isConfidential, query, subtype),
    queryFn: () =>
      documentsApi.getDocumentsAggregations({
        // subtags pins the active sub-type chip at zero results; the backend does NOT narrow
        // count/tabs by it (guarantee-include, not a filter — da-19295 design D9 / DA-19691).
        query: { ...query, isConfidential, category, ...(subtype ? { subtags: [subtype] } : {}) },
      }),
  });

// Gated on categorization availability (Bookkeeper), like the sub-type catalog; disabled
// until a companyId is present. `extraEnabled` composes an extra condition (the
// accessor passes isGrid so the query fires only in grid view). Returns already-normalized arrays
// so consumers never touch `.data?` — `count` stays null while unknown (distinct from a real 0, so
// the deferred empty-redirect can't false-fire during load).
export const useDocumentsAggregations = (
  params: DocumentsAggregationsParams,
  extraEnabled = true,
  // Tile-side block used as a stand-in while the grid query is cold — same entities as the grid
  // response (shared DoctypeSchema / SearchSubtypeChipSchema, see schemas.ts).
  placeholder?: { tabs: SearchDoctype[]; subtypes: SearchSubtypeChip[] },
) => {
  const allowed = useSelector(documentCategorizationAllowedSelector);
  const { data, isInitialLoading, isPlaceholderData } = useQuery({
    ...documentsAggregationsQuery(params),
    enabled: allowed && extraEnabled && Boolean(params.companyId),
    // DA-19691: keep the previous category/sub-type tabs+chips rendered during the refetch so the
    // whole block does not unmount → collapse → remount (grid-only flicker; the key now also changes
    // on sub-type switch). TanStack v4 API.
    keepPreviousData: true,
    // DA-19731: on a tile→grid switch this query mounts COLD (it is `enabled: isGrid`), so `data` is
    // undefined for a whole round-trip and the tabs+chips block collapsed. keepPreviousData cannot
    // cover that: it carries data across a KEY change on an already-enabled observer, and here
    // there is no previous data at all. The tile Redux block describes the same category under the
    // same global filter, so it is a valid stand-in. Deliberately `placeholderData`, NOT
    // queryClient.setQueryData: nothing is written into the cache (domain/CLAUDE.md forbids pushing
    // saga-owned data into a TanStack cache), and a failed fetch still surfaces as status 'error'
    // instead of silently showing stale tile data forever.
    // `count: 0` is filler the response schema demands (it types `count` as a number); the real
    // count is unknown while the stand-in is showing, and the return below hands out `null` for it
    // rather than leaking this 0.
    ...(placeholder ? { placeholderData: { ...placeholder, count: 0 } } : {}),
  });
  return {
    tabs: data?.tabs ?? EMPTY_TABS,
    subtypes: data?.subtypes ?? EMPTY_SUBTYPES,
    // `count` is genuinely unknown while the tile stand-in is on screen, so keep the documented
    // "null while unknown, never a fake 0" contract instead of surfacing the placeholder filler.
    // The backend does return a real count here — see DocumentsAggregationsResponseSchema — so this
    // stays exposed even though nothing reads it today.
    count: isPlaceholderData ? null : data?.count ?? null,
    // `isInitialLoading`, NOT `isLoading`: in react-query v4 a DISABLED query still reports
    // `status: 'loading'` (v4 migration guide — "Disabled queries […] will now initiate in a
    // 'loading' state. Use isInitialLoading"), and this query is disabled on tile and whenever the
    // feature gate or companyId is missing. Keying a skeleton off `isLoading` would leave it
    // spinning forever in exactly those cases. `isInitialLoading` is `isLoading && isFetching`, i.e.
    // "a first fetch is actually in flight" — the real busy marker.
    isLoading: isInitialLoading,
  };
};
